import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaRocket,
  FaBuilding,
  FaCity,
  FaUtensils,
  FaTshirt,
  FaHeartbeat,
  FaGraduationCap,
  FaHome,
  FaUserTie,
  FaChartLine,
  FaUsers,
  FaHeart,
  FaInstagram,
  FaCheckCircle,
  FaArrowRight,
  FaWhatsapp
} from 'react-icons/fa';
import { HiSparkles, HiLightningBolt, HiTrendingUp, HiBadgeCheck } from 'react-icons/hi';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const IndustriesPage = () => {
  const [activeIndustry, setActiveIndustry] = useState(0);

  // WhatsApp configuration
  const whatsappNumber = '918700186979';
  const whatsappMessage = 'Hello! I would like to discuss digital marketing for my industry.';
  const openWhatsApp = () => {
    window.open(`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`, '_blank');
  };

  const industries = [
    {
      icon: FaRocket,
      title: 'Startups',
      description: 'Launch and scale your startup with growth-focused digital strategies',
      services: ['Brand Building', 'Social Media', 'Performance Marketing', 'PR & Visibility'],
      color: 'from-orange-500 to-red-600',
      bgColor: 'bg-orange-500',
    },
    {
      icon: FaBuilding,
      title: 'SMEs',
      description: 'Accelerate growth for small and medium enterprises with targeted campaigns',
      services: ['Lead Generation', 'SEO', 'Content Marketing', 'Paid Advertising'],
      color: 'from-blue-500 to-indigo-600',
      bgColor: 'bg-blue-500',
    },
    {
      icon: FaCity,
      title: 'Large Enterprises',
      description: 'Enterprise-level digital transformation and brand management',
      services: ['Multi-channel Strategy', 'Brand Management', 'Analytics', 'Global Campaigns'],
      color: 'from-slate-600 to-slate-800',
      bgColor: 'bg-slate-600',
    },
    {
      icon: FaUtensils,
      title: 'Restaurants & Cafes',
      description: 'Drive footfall and orders with mouth-watering digital presence',
      services: ['Local SEO', 'Food Photography', 'Influencer Marketing', 'Delivery Platform Ads'],
      color: 'from-amber-500 to-orange-600',
      bgColor: 'bg-amber-500',
    },
    {
      icon: FaTshirt,
      title: 'Fashion & Lifestyle',
      description: 'Build aspirational brands that connect with style-conscious audiences',
      services: ['Influencer Campaigns', 'E-commerce Marketing', 'Visual Content', 'Trend Marketing'],
      color: 'from-pink-500 to-rose-600',
      bgColor: 'bg-pink-500',
    },
    {
      icon: FaHeartbeat,
      title: 'Healthcare',
      description: 'Ethical and compliant healthcare marketing that builds trust',
      services: ['Patient Acquisition', 'Reputation Management', 'Educational Content', 'Local SEO'],
      color: 'from-teal-500 to-cyan-600',
      bgColor: 'bg-teal-500',
    },
    {
      icon: FaGraduationCap,
      title: 'Education',
      description: 'Attract students and build authority in the education sector',
      services: ['Lead Generation', 'Content Marketing', 'Social Proof', 'Webinar Marketing'],
      color: 'from-purple-500 to-violet-600',
      bgColor: 'bg-purple-500',
    },
    {
      icon: FaHome,
      title: 'Real Estate',
      description: 'Generate quality leads and showcase properties effectively',
      services: ['Lead Generation', 'Virtual Tours', 'Property Listings', 'Geo-targeted Ads'],
      color: 'from-green-500 to-emerald-600',
      bgColor: 'bg-green-500',
    },
    {
      icon: FaUserTie,
      title: 'Personal Brands',
      description: 'Build thought leadership and personal brand authority',
      services: ['LinkedIn Marketing', 'Content Strategy', 'PR & Media', 'Speaking Opportunities'],
      color: 'from-indigo-500 to-purple-600',
      bgColor: 'bg-indigo-500',
    },
  ];

  const stats = [
    { value: '300%', label: 'Average ROI increase for e-commerce clients', icon: FaChartLine, color: 'from-green-500 to-emerald-600' },
    { value: '5M+', label: 'Combined reach achieved for fashion brands', icon: FaUsers, color: 'from-pink-500 to-rose-600' },
    { value: '200+', label: 'Monthly leads for healthcare providers', icon: FaHeart, color: 'from-red-500 to-rose-600' },
    { value: '15K', label: 'Instagram growth for restaurant clients', icon: FaInstagram, color: 'from-purple-500 to-pink-600' },
  ];

  const whyExpertise = [
    {
      icon: HiLightningBolt,
      title: 'Deep Market Understanding',
      description: 'We know your audience, competitors, and trends',
      color: 'from-amber-500 to-orange-600',
    },
    {
      icon: HiBadgeCheck,
      title: 'Tailored Strategies',
      description: 'No generic approaches, only customized solutions',
      color: 'from-blue-500 to-indigo-600',
    },
    {
      icon: HiTrendingUp,
      title: 'Faster Results',
      description: 'Experience means we hit the ground running',
      color: 'from-green-500 to-emerald-600',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.08, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  return (
    <div className="min-h-screen overflow-x-hidden">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-24 overflow-hidden bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 60, repeat: Infinity, ease: 'linear' }}
            className="absolute -top-1/2 -left-1/2 w-full h-full"
          >
            <div className="absolute top-1/3 left-1/3 w-[600px] h-[600px] bg-purple-500/10 rounded-full blur-[150px]" />
            <div className="absolute bottom-1/3 right-1/3 w-[500px] h-[500px] bg-pink-500/10 rounded-full blur-[150px]" />
          </motion.div>

          {/* Industry Icons Floating */}
          {industries.slice(0, 6).map((industry, i) => (
            <motion.div
              key={i}
              className="absolute"
              style={{
                left: `${10 + i * 15}%`,
                top: `${15 + (i % 3) * 25}%`,
              }}
              animate={{
                y: [-15, 15, -15],
                x: [-5, 5, -5],
                rotate: [0, 5, -5, 0],
                opacity: [0.2, 0.4, 0.2],
              }}
              transition={{
                duration: 5 + i,
                repeat: Infinity,
                delay: i * 0.5,
              }}
            >
              <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${industry.color} opacity-30 flex items-center justify-center`}>
                <industry.icon className="text-white text-xl" />
              </div>
            </motion.div>
          ))}
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-8"
            >
              <HiSparkles className="text-yellow-400 text-lg" />
              <span className="text-white font-semibold text-sm">Industry Expertise</span>
            </motion.div>

            {/* Main Heading */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6"
            >
              Industries We{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-orange-400">
                Serve
              </span>
            </motion.h1>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-xl text-slate-300 leading-relaxed max-w-3xl mx-auto mb-16"
            >
              Tailored digital strategies for diverse industries, from startups to enterprises
            </motion.p>
          </motion.div>
        </div>

        {/* Bottom Wave */}
        <div className="absolute -bottom-1 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" className="w-full h-16">
            <path d="M0,64L48,69.3C96,75,192,85,288,80C384,75,480,53,576,48C672,43,768,53,864,64C960,75,1056,85,1152,80C1248,75,1344,53,1392,42.7L1440,32L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z" fill="white" />
          </svg>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="py-20 bg-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-bl from-purple-100/50 to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-tr from-pink-100/50 to-transparent rounded-full blur-3xl" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {industries.map((industry, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ y: -10, scale: 1.02 }}
                onHoverStart={() => setActiveIndustry(index)}
                className="group relative cursor-pointer"
              >
                {/* Glow Effect */}
                <div className={`absolute -inset-1 bg-gradient-to-r ${industry.color} rounded-3xl opacity-0 group-hover:opacity-60 blur-xl transition-all duration-500`} />

                <div className="relative bg-white rounded-3xl p-8 shadow-lg border border-slate-100 group-hover:border-transparent group-hover:shadow-2xl transition-all duration-500 h-full">
                  {/* Icon */}
                  <motion.div
                    whileHover={{ rotate: [0, -10, 10, 0], scale: 1.1 }}
                    transition={{ duration: 0.5 }}
                    className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${industry.color} flex items-center justify-center mb-6 shadow-lg group-hover:shadow-xl transition-shadow`}
                  >
                    <industry.icon className="text-2xl text-white" />
                  </motion.div>

                  {/* Title */}
                  <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-slate-900 group-hover:to-slate-600">
                    {industry.title}
                  </h3>

                  {/* Description */}
                  <p className="text-slate-600 text-sm mb-5 leading-relaxed">
                    {industry.description}
                  </p>

                  {/* Services Tags */}
                  <div className="flex flex-wrap gap-2 mb-6">
                    {industry.services.map((service, i) => (
                      <span
                        key={i}
                        className="px-3 py-1 bg-slate-100 group-hover:bg-slate-800 group-hover:text-white text-slate-600 text-xs font-medium rounded-full transition-all duration-300 shadow-sm"
                      >
                        {service}
                      </span>
                    ))}
                  </div>

                  {/* Learn More */}
                  <div className="flex items-center gap-2 text-slate-500 group-hover:text-slate-900 transition-colors">
                    <span className="text-sm font-semibold">Explore Solutions</span>
                    <FaArrowRight className="text-xs group-hover:translate-x-2 transition-transform" />
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-2 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-full text-purple-600 text-sm font-semibold mb-4">
              Proven Results
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Proven Results{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
                Across Industries
              </span>
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              We've helped businesses of all sizes achieve remarkable growth
            </p>
          </motion.div>

          {/* Stats Grid */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ y: -10, scale: 1.05 }}
                className="group"
              >
                <div className="relative bg-white rounded-3xl p-8 shadow-lg border border-slate-100 group-hover:shadow-2xl transition-all duration-500 text-center overflow-hidden">
                  {/* Background Gradient on Hover */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />

                  <div className="relative z-10">
                    {/* Icon */}
                    <div className={`w-14 h-14 mx-auto mb-4 rounded-2xl bg-gradient-to-br ${stat.color} group-hover:bg-white/20 flex items-center justify-center transition-colors`}>
                      <stat.icon className="text-2xl text-white" />
                    </div>

                    {/* Value */}
                    <motion.h3
                      initial={{ scale: 1 }}
                      whileHover={{ scale: 1.1 }}
                      className="text-4xl md:text-5xl font-black text-slate-900 group-hover:text-white mb-2 transition-colors"
                    >
                      {stat.value}
                    </motion.h3>

                    {/* Label */}
                    <p className="text-slate-600 group-hover:text-white/90 text-sm leading-relaxed transition-colors">
                      {stat.label}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Why Industry Expertise Matters */}
      <section className="py-20 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-[150px]" />
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-pink-500/10 rounded-full blur-[150px]" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-2 bg-white/10 rounded-full text-purple-400 text-sm font-semibold mb-4">
              The Advantage
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Why Industry Expertise{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
                Matters
              </span>
            </h2>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid md:grid-cols-3 gap-8"
          >
            {whyExpertise.map((item, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ y: -10, scale: 1.02 }}
                className="group relative"
              >
                {/* Glow Effect */}
                <div className={`absolute -inset-1 bg-gradient-to-r ${item.color} rounded-3xl opacity-0 group-hover:opacity-50 blur-xl transition-all duration-500`} />

                <div className="relative bg-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/10 group-hover:border-white/20 transition-all h-full text-center">
                  {/* Number Badge */}
                  <div className={`absolute -top-4 left-1/2 -translate-x-1/2 w-10 h-10 bg-gradient-to-br ${item.color} rounded-full flex items-center justify-center text-white font-bold shadow-lg`}>
                    {index + 1}
                  </div>

                  {/* Icon */}
                  <div className={`w-20 h-20 mx-auto mt-4 rounded-2xl bg-gradient-to-br ${item.color} flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform`}>
                    <item.icon className="text-3xl text-white" />
                  </div>

                  {/* Content */}
                  <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                  <p className="text-slate-400 leading-relaxed">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Interactive Industry Selector */}
      <section className="py-20 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Find Your{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
                Industry
              </span>
            </h2>
            <p className="text-slate-600 text-lg">Select an industry to see our tailored approach</p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Industry Tabs */}
            <div className="flex flex-wrap justify-center lg:flex-col gap-3">
              {industries.map((industry, index) => (
                <motion.button
                  key={index}
                  onClick={() => setActiveIndustry(index)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`flex items-center gap-3 px-5 py-3 rounded-xl transition-all duration-300 cursor-pointer ${activeIndustry === index
                    ? `bg-gradient-to-r ${industry.color} text-white shadow-lg`
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                >
                  <industry.icon className="text-lg" />
                  <span className="font-semibold text-sm">{industry.title}</span>
                </motion.button>
              ))}
            </div>

            {/* Industry Detail */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeIndustry}
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.3 }}
                className={`bg-gradient-to-br ${industries[activeIndustry].color} rounded-3xl p-8 md:p-10 shadow-2xl`}
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                    {React.createElement(industries[activeIndustry].icon, { className: 'text-3xl text-white' })}
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-white">
                    {industries[activeIndustry].title}
                  </h3>
                </div>

                <p className="text-white/90 text-lg mb-8 leading-relaxed">
                  {industries[activeIndustry].description}
                </p>

                <div className="mb-8">
                  <h4 className="text-white/70 text-sm font-semibold uppercase tracking-wider mb-4">
                    Our Services for {industries[activeIndustry].title}
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    {industries[activeIndustry].services.map((service, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.1 }}
                        className="flex items-center gap-2"
                      >
                        <FaCheckCircle className="text-white/80" />
                        <span className="text-white font-medium">{service}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>

                <motion.button
                  onClick={openWhatsApp}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full py-4 bg-white text-slate-900 font-bold rounded-2xl flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition-shadow cursor-pointer"
                >
                  <FaWhatsapp className="text-xl text-green-500" />
                  Get {industries[activeIndustry].title} Strategy
                </motion.button>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-purple-500/5 via-pink-500/5 to-orange-500/5 rounded-full blur-3xl" />
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <motion.div
              animate={{ y: [-5, 5, -5] }}
              transition={{ duration: 3, repeat: Infinity }}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-full border border-purple-500/20 mb-8"
            >
              <HiSparkles className="text-purple-600" />
              <span className="text-purple-700 font-medium">Industry Experts</span>
            </motion.div>

            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Ready to Dominate{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
                Your Industry?
              </span>
            </h2>
            <p className="text-xl text-slate-600 mb-10 max-w-2xl mx-auto">
              Let's create a customized strategy tailored to your industry's unique challenges
            </p>

            <motion.button
              onClick={openWhatsApp}
              whileHover={{ scale: 1.05, boxShadow: '0 25px 50px -12px rgba(168, 85, 247, 0.4)' }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold text-lg rounded-full shadow-2xl transition-all cursor-pointer group"
            >
              <FaWhatsapp className="text-2xl" />
              Get Industry Strategy
              <motion.span
                animate={{ x: [0, 5, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <FaArrowRight className="group-hover:translate-x-2 transition-transform" />
              </motion.span>
            </motion.button>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default IndustriesPage;
